import React from "react";
import {
  ArrowRight,
  Briefcase,
  Calendar,
  CheckCircle,
  FileText,
  Home,
  Loader2,
  MapPin,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { QuizQuestion } from "./QuizShell";
import { OptionCard } from "./OptionCard";
import type { QuizProgress } from "./types";

export interface TypeQuizProps {
  progress: QuizProgress;
  campaignType: "property" | "service" | null;
  onSelectType: (t: "property" | "service") => void;
  hasDraft: boolean;
  propertyDraft: any | null;
  serviceDraft: any | null;
  draft: any | null;
  setDraft: (d: any) => void;
  setCampaignType: (t: "property" | "service") => void;
  showDraftPreview: boolean;
  setShowDraftPreview: (v: boolean) => void;
  isDraftLoading: boolean;
  isDraftDeleting: boolean;
  onUseDraft: () => void;
  onDeleteDraft: () => void;
  launchSteps: { title: string; subtitle: string }[];
}

const draftTitle = (d: any) =>
  d.title ||
  d.headline ||
  d.campaign_name ||
  d.address ||
  (d.campaign_type === "service" ? "Untitled service" : "Untitled property");

export const TypeQuiz: React.FC<TypeQuizProps> = ({
  progress,
  campaignType,
  onSelectType,
  hasDraft,
  propertyDraft,
  serviceDraft,
  draft,
  setDraft,
  setCampaignType,
  showDraftPreview,
  setShowDraftPreview,
  isDraftLoading,
  isDraftDeleting,
  onUseDraft,
  onDeleteDraft,
  launchSteps,
}) => {
  const position = progress.before + 1;
  const total = progress.before + 1 + progress.after;

  // A draft was opened: show its summary with Continue / Delete.
  if (showDraftPreview) {
    const d = draft;
    const savedStep = d ? d.current_step || d.currentStep || d.step || 5 : 5;
    const img = d
      ? d.image_url || d.image || d.heroImage || d.hero_image || d.photos?.[0] || null
      : null;
    return (
      <QuizQuestion
        position={position}
        total={total}
        title="Pick up where you left off"
        hint="Your saved draft is ready to continue."
        onBack={() => setShowDraftPreview(false)}
        backLabel="Start something new"
        size="md"
      >
        {isDraftLoading || !d ? (
          <div className="flex items-center justify-center rounded-2xl border bg-white py-14">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <span className="ml-3 text-slate-600">Loading your draft…</span>
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-blue-200 bg-white shadow-md">
            <div className="flex items-center gap-3 bg-gradient-to-r from-blue-500 to-indigo-600 p-4 text-white">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-lg font-bold">
                {savedStep}
              </span>
              <div className="min-w-0">
                <p className="truncate font-semibold">{launchSteps[savedStep - 1]?.title || "Saved step"}</p>
                <p className="truncate text-sm text-blue-100">{launchSteps[savedStep - 1]?.subtitle}</p>
              </div>
              <button
                type="button"
                onClick={() => setShowDraftPreview(false)}
                className="ml-auto rounded-full p-1.5 hover:bg-white/20"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex items-start gap-4 p-5">
              <div className="h-20 w-24 shrink-0 overflow-hidden rounded-xl bg-slate-100">
                {img ? (
                  <img src={img} alt="" className="h-full w-full object-cover" onError={(e) => ((e.target as HTMLImageElement).style.display = "none")} />
                ) : (
                  <div className="flex h-full items-center justify-center">
                    {d.campaign_type === "service" ? (
                      <Briefcase className="h-8 w-8 text-purple-300" />
                    ) : (
                      <Home className="h-8 w-8 text-blue-300" />
                    )}
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <Badge className={d.campaign_type === "service" ? "bg-purple-500" : "bg-blue-500"}>
                    {d.campaign_type === "service" ? "Service" : "Property"}
                  </Badge>
                  <Badge className="bg-amber-500">Draft</Badge>
                  {d.time_since_update && (
                    <span className="flex items-center gap-1 text-xs text-slate-500">
                      <Calendar className="h-3 w-3" />
                      {d.time_since_update}
                    </span>
                  )}
                </div>
                <p className="truncate text-lg font-bold text-slate-900">{draftTitle(d)}</p>
                {d.campaign_type !== "service" && d.address && (
                  <p className="mt-0.5 flex items-center gap-1 text-sm text-slate-600">
                    <MapPin className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{d.address}</span>
                  </p>
                )}
                {d.campaign_type === "service" && d.service_type && (
                  <p className="mt-0.5 text-sm font-medium text-purple-600">{d.service_type}</p>
                )}
              </div>
            </div>
            <div className="mx-5 mb-5 rounded-xl bg-slate-50 p-3">
              <div className="mb-1 flex justify-between text-xs font-medium text-slate-500">
                <span>Progress</span>
                <span>
                  Step {savedStep} of {launchSteps.length}
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-slate-200">
                <div className="h-full rounded-full bg-gradient-to-r from-blue-400 to-blue-600" style={{ width: `${(savedStep / launchSteps.length) * 100}%` }} />
              </div>
              <ul className="mt-3 grid gap-1 text-xs text-slate-600 sm:grid-cols-2">
                {savedStep >= 2 && (d.title || d.address) && (
                  <li className="flex items-center gap-1"><CheckCircle className="h-3 w-3 text-green-500" />{d.campaign_type === "service" ? "Service details" : "Property details"}</li>
                )}
                {savedStep >= 3 && d.selected_template_id && (
                  <li className="flex items-center gap-1"><CheckCircle className="h-3 w-3 text-green-500" />Template selected</li>
                )}
                {savedStep >= 4 && (d.subject || d.campaign_subject) && (
                  <li className="flex items-center gap-1"><CheckCircle className="h-3 w-3 text-green-500" />Subject line</li>
                )}
                {savedStep >= 5 && (d.selected_datasets?.length > 0 || d.selected_custom_datasets?.length > 0) && (
                  <li className="flex items-center gap-1"><CheckCircle className="h-3 w-3 text-green-500" />Recipients</li>
                )}
                {savedStep >= 6 && d.schedule_option && (
                  <li className="flex items-center gap-1"><CheckCircle className="h-3 w-3 text-green-500" />Schedule: {d.schedule_option === "now" ? "Send now" : "Scheduled"}</li>
                )}
              </ul>
            </div>
            <div className="flex items-center gap-3 border-t p-4">
              <Button variant="ghost" onClick={onDeleteDraft} disabled={isDraftDeleting} className="text-red-600 hover:bg-red-50">
                {isDraftDeleting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1.5 h-4 w-4" />}
                Delete
              </Button>
              <Button onClick={onUseDraft} className="ml-auto h-11 flex-1 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white sm:flex-none sm:px-8">
                <Sparkles className="mr-2 h-4 w-4" />
                Continue from step {savedStep}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </QuizQuestion>
    );
  }

  const openDraft = (d: any, type: "property" | "service") => {
    setDraft(d);
    setCampaignType(type);
    setShowDraftPreview(true);
  };

  return (
    <QuizQuestion
      position={position}
      total={total}
      title="What are you launching today?"
      hint="Pick one to get started — we'll guide you step by step."
      size="md"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <OptionCard
          icon={Home}
          tone="blue"
          title="Property launch"
          description="Promote a property listing."
          selected={campaignType === "property"}
          onClick={() => onSelectType("property")}
        />
        <OptionCard
          icon={Briefcase}
          tone="violet"
          title="Service launch"
          description="Promote a service business."
          selected={campaignType === "service"}
          onClick={() => onSelectType("service")}
        />
      </div>

      {hasDraft && (propertyDraft || serviceDraft) && (
        <div className="mt-10">
          <p className="mb-3 text-center text-sm font-semibold uppercase tracking-wide text-slate-400">
            Or continue a saved draft
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {propertyDraft && (
              <OptionCard
                layout="row"
                icon={FileText}
                tone="blue"
                title="Property draft"
                description={draftTitle(propertyDraft)}
                onClick={() => openDraft(propertyDraft, "property")}
              />
            )}
            {serviceDraft && (
              <OptionCard
                layout="row"
                icon={FileText}
                tone="emerald"
                title="Service draft"
                description={draftTitle(serviceDraft)}
                onClick={() => openDraft(serviceDraft, "service")}
              />
            )}
          </div>
        </div>
      )}
    </QuizQuestion>
  );
};
